import { listCommunityMembers } from "client/Api/Communities/community";
import { UserIdentityModel } from "client/Api/UserProfile/service";
import {
	Dialog,
	FormattedText,
	LoadMoreIndicator,
	NoResults
} from "client/Components";
import {
	createRemoteDataSource,
	LazyLoadCursorKind,
	NumberFormatter,
	RemoteDataSourceState,
	useEffectSkipFirstRender,
	useOpenDialog
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { useHubStatisticsFormatter } from "../useHubStatisticsFormatter";
import {
	generateTrackingId,
	setTrackingId
} from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { UserListItem } from "client/Features/Users/UserListItem/UserListItem";
import styles from "./CommunityMembersDialog.module.less";

export type CommunityMembersDialogProps = {
	communityId: number;
	membersCount: number;
	onClose: () => void;
};

export const CommunityMembersDialog = (props: CommunityMembersDialogProps): ReactElement => {
	const {
		communityId,
		membersCount,
		onClose
	} = props;

	const [desiredAmountOfItems, setDesiredAmountOfItems] = useState<number>(PAGE_SIZE);

	const statisticsFormatter: NumberFormatter = useHubStatisticsFormatter();

	const {
		hasLazyLoadError,
		hasMoreOnServer,
		currentItems,
		getLoadingState,
		reload,
		retryToLoadLastPage
	} = useDataSource({
		globalContext: {
			communityId
		},
		desiredAmountOfItems
	});

	const loadingState: RemoteDataSourceState = getLoadingState();

	const increaseAmount = useCallback(
		(): void => {
			const areAllPreviouslyRequestedItemsLoaded: boolean = currentItems.length >= desiredAmountOfItems;

			if (loadingState !== "loading"
				&& areAllPreviouslyRequestedItemsLoaded
				&& hasMoreOnServer
				&& !hasLazyLoadError
			) {
				setDesiredAmountOfItems(prevState => prevState + PAGE_SIZE);
			}
		},
		[
			currentItems.length,
			desiredAmountOfItems,
			hasLazyLoadError,
			hasMoreOnServer,
			loadingState
		]
	);

	useEffectSkipFirstRender(
		(): void => {
			hasImplicitDependencyOn(communityId);
			setDesiredAmountOfItems(PAGE_SIZE);
		},
		[communityId]
	);

	const renderContent = (): ReactElement | null => {
		if (currentItems.length > 0 || loadingState === "loading") {
			return (
				<>
					<List
						items={currentItems}
						hasMoreOnServer={hasMoreOnServer}
						isLoading={loadingState === "loading"}
						onRequestMoreItems={increaseAmount}
					/>
					{hasLazyLoadError && (
						<NoResults
							trackingId="not-able-to-retrieve-members"
							icon="gear"
							title={localize("_NotAbleToRetrieveMembers")}
							message={localize("_TheMembersCouldNotBeRetrieved")}
							onRetry={retryToLoadLastPage}
							hasExtraTopPadding
						/>
					)}
				</>
			);
		}

		if (loadingState === "error") {
			return (
				<NoResults
					trackingId="not-able-to-retrieve-members"
					icon="gear"
					title={localize("_NotAbleToRetrieveMembers")}
					message={localize("_TheMembersCouldNotBeRetrieved")}
					onRetry={reload}
					height="fill"
				/>
			);
		}

		return (
			<NoResults
				trackingId="no-members"
				icon="user-group"
				title={localize("_NoMembersYet")}
				message={localize("_ThereAreNoMembersInThisCommunityYet")}
				height="fill"
			/>
		);
	};

	return (
		<>
			<Dialog
				trackingId="community-members-dialog"
				onClose={onClose}
				height="fill"
			>
				<Dialog.Header
					title={(
						<FormattedText
							text={localize("_TextWithClarificationFormat")}
							thingsToReplace={[
								{
									what: "{{Text}}",
									filler: localize("_Members")
								},
								{
									what: "{{Clarification}}",
									filler: statisticsFormatter(membersCount)
								}
							]}
						/>
					)}
					onClose={onClose}
				/>
				<Dialog.Body goesEdgeToEdge>
					{renderContent()}
				</Dialog.Body>
				<Dialog.Footer />
			</Dialog>
		</>
	);
};
CommunityMembersDialog.displayName = "CommunityMembersDialog";

type ListProps = {
	items: readonly UserIdentityModel[];
	isLoading: boolean;
	hasMoreOnServer: boolean;
	onRequestMoreItems: () => void;
};

const List = (props: ListProps): ReactElement => {
	const {
		items,
		hasMoreOnServer,
		isLoading,
		onRequestMoreItems
	} = props;

	return (
		<ul
			{...setTrackingId("members-list")}
			className={styles.list}
		>
			{items.map(
				x => (
					<li key={x.userId}>
						<UserListItem
							user={x}
							trackingId={generateTrackingId("user-item", `|${x.userId}`)}
							noMobileBorderBottom
						/>
					</li>
				)
			)}
			<li>
				<LoadMoreIndicator
					isLoadingData={isLoading}
					hasMoreItems={hasMoreOnServer}
					onRequestMoreItems={onRequestMoreItems}
				/>
			</li>
		</ul>
	);
};

const PAGE_SIZE: number = 50;

type GlobalContex = {
	communityId: number;
};

const useDataSource = createRemoteDataSource<UserIdentityModel, undefined, GlobalContex>()
	.setConfiguration({
		lazyLoadCursor: LazyLoadCursorKind.SkipTake
	}).getHook({
		performRequest: (options) => {
			const {
				globalContext,
				skip,
				amount,
				signal
			} = options;

			const { communityId } = globalContext;

			return listCommunityMembers(
				communityId,
				{
					take: amount,
					skip
				},
				signal
			);
		}
	});

export function useCommunityMembersDialog(): [
	element: ReactElement | null,
	open: (props: Omit<CommunityMembersDialogProps, "onClose">) => Promise<void>
] {
	return useOpenDialog(
		(props, onClose) => (
			<CommunityMembersDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}